import type { HoComponents, SessionDto } from '@pos/api-client';
import { hasPermission } from '@pos/admin-ui';
import { hoApi } from '../api';

export type ImageAsset = HoComponents['schemas']['AssetDto'];

export const imagesKey = ['ho', 'images'] as const;

/** The screens that pick pictures; the server checks these again (any one of each list is enough, FR-ONL-17, ADR 0066, ADR 0116). */
const IMAGE_SCREENS = [
  'ho.catalog.items',
  'ho.catalog.categories',
  'ho.catalog.item_groups',
  'ho.catalog.modifier_groups',
  'ho.catalog.deals',
  'ho.online.brand_sites',
  'ho.org.brands',
] as const;
export const IMAGE_READ_PERMISSIONS = IMAGE_SCREENS.map((s) => `${s}.read`);
export const IMAGE_MANAGE_PERMISSIONS = IMAGE_SCREENS.map((s) => `${s}.manage`);

/** The images the server accepts; it turns them into WebP. */
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', '.jpg', '.jpeg', '.png', '.webp', '.gif'];
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024;

const hasAny = (session: SessionDto | undefined, permissions: readonly string[]) =>
  permissions.some((p) => hasPermission(session, p));

export const canReadImages = (session: SessionDto | undefined) => hasAny(session, IMAGE_READ_PERMISSIONS);
export const canManageImages = (session: SessionDto | undefined) => hasAny(session, IMAGE_MANAGE_PERMISSIONS);

/** One permission the side nav can check for the page: the first read permission the user has. */
export const imagesNavPermission = (session: SessionDto | undefined) =>
  IMAGE_READ_PERMISSIONS.find((p) => hasPermission(session, p)) ?? `${IMAGE_SCREENS[0]}.read`;

/** Newest first; with a brand, that brand's images and those for every brand; with a search, by file name or description. */
export async function listImages(query: { brandId?: string; search?: string; page: number; pageSize: number }) {
  const { data, error } = await hoApi.GET('/api/v1/identity/assets', {
    params: {
      query: { brandId: query.brandId || undefined, search: query.search || undefined, page: query.page, pageSize: query.pageSize },
    },
  });
  if (!data) throw error;
  return data;
}

/**
 * Uploads one image. `existing` is true when the same image was already uploaded (200): the answer is that one, not a
 * copy.
 */
export async function uploadImage(file: File, options: { brandId?: string; altText?: string } = {}) {
  const form = new FormData();
  form.append('file', file, file.name);
  if (options.brandId) form.append('brandId', options.brandId);
  if (options.altText?.trim()) form.append('altText', options.altText.trim());
  const { data, error, response } = await hoApi.POST('/api/v1/identity/assets', {
    // openapi-fetch sends FormData as it is and lets the browser set the multipart boundary.
    body: form as never,
  });
  if (!data) throw error;
  return { image: data, existing: response.status === 200 };
}

/** Hides it from the list and the pickers; its file stays, so pages that still show it keep working. */
export async function deleteImage(id: string) {
  const { error, response } = await hoApi.DELETE('/api/v1/identity/assets/{id}', { params: { path: { id } } });
  if (!response.ok) throw error;
}
